import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import SortableListItem from './SortableItem';
import AurSearchBox from '../../common/aur-search-box/AurSearchBox';
import {ReactComponent as OrderIcon} from '../../../assests/svg/order_icon.svg';
import {t} from 'i18next';
import {ArrayItemType} from '../eca-doc-list.util';
import {HandleDrag} from './HandleDrag';
import {isEmpty} from '../../../utils/utils';

import {ReactComponent as ArrowRightOutlinedIcon} from '../../../assests/svg/right-icon-large.svg';
import {ReactComponent as ArrowRightOutlinedIconActive} from '../../../assests/svg/right-icon-large-active.svg';
import {ReactComponent as ArrowDoubbleRight} from '../../../assests/svg/double-right-icon.svg';
import {ReactComponent as ArrowDoubbleRightInactive} from '../../../assests/svg/double-icon-right-inactive.svg';
import {ReactComponent as ArrowLeftOutlinedIcon} from '../../../assests/svg/left-icon-large.svg';
import {ReactComponent as ArrowLeftOutlinedIconActive} from '../../../assests/svg/left-icon-large-active.svg';
import {ReactComponent as ArrowDoubbleLeft} from '../../../assests/svg/double-left-icon.svg';
import {ReactComponent as ArrowDoubbleLeftActive} from '../../../assests/svg/double-left-active.svg';
import {difference, differenceBy, intersection, union} from 'lodash';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';
import {useAnalytics} from '../../../hooks/useAnalytics';

type ManageColFieldtType = 'available' | 'selected';

type ManageColFieldsProps = {
  getSelectedList: (list: ArrayItemType[]) => void;
  selectedList: ArrayItemType[];
  availableList: ArrayItemType[];
};

export function not(a: ArrayItemType[], b: ArrayItemType[]) {
  return a.filter(({name: aId}) => !b.some(({name: bId}) => aId === bId));
}

const ManageColFields = ({
  getSelectedList,
  selectedList,
  availableList,
}: ManageColFieldsProps) => {
  const [selectedFields, setSelectedFields] =
    useState<ArrayItemType[]>(selectedList);
  const {trackGAEvent} = useAnalytics();

  const [selectedAvailable, setSelectedAvailable] = useState<ArrayItemType[]>(
    []
  );
  const [selectedSelected, setSelectedSelected] = useState<ArrayItemType[]>([]);

  const [searchTermAvailable, setSearchTermAvailable] = useState('');
  const [searchTermSelected, setSearchTermSelected] = useState('');

  useEffect(() => {
    if (selectedFields?.length) {
      getSelectedList?.(selectedFields);
    }
  }, [getSelectedList, selectedFields]);

  useEffect(() => {
    if (!isEmpty(differenceBy(selectedFields, selectedFields, 'id'))) {
      getSelectedList(selectedFields);
    }
  }, [getSelectedList, selectedFields]);

  const availableFields = useMemo(() => {
    const selectedKeys = new Set(selectedFields.map((s) => s.name));
    return availableList.filter((field) => !selectedKeys.has(field.name));
  }, [availableList, selectedFields]);

  const displayedAvailableFields = useMemo(() => {
    return availableFields
      .filter((field) =>
        !isEmpty(searchTermAvailable?.trim())
          ? field.label
              .toLowerCase()
              .includes(searchTermAvailable.toLowerCase())
          : true
      )
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [availableFields, searchTermAvailable]);

  const displayedSelectedFields = useMemo(() => {
    return selectedFields.filter((field) =>
      !isEmpty(searchTermSelected?.trim())
        ? field.label.toLowerCase().includes(searchTermSelected.toLowerCase())
        : true
    );
  }, [selectedFields, searchTermSelected]);

  const commonAvailableFields = useMemo(() => {
    return intersection(availableFields, displayedAvailableFields);
  }, [availableFields, displayedAvailableFields]);

  const commonSelectedFields = useMemo(() => {
    return intersection(selectedFields, displayedSelectedFields);
  }, [displayedSelectedFields, selectedFields]);

  const commonSelectedAvailableFields = useMemo(() => {
    return intersection(selectedAvailable, displayedAvailableFields);
  }, [displayedAvailableFields, selectedAvailable]);

  const commonSelectedSelectedFields = useMemo(() => {
    return intersection(selectedSelected, displayedSelectedFields);
  }, [displayedSelectedFields, selectedSelected]);

  const moveToAllSelected = useCallback(() => {
    const updatedSelectedFields = union(selectedFields, commonAvailableFields);
    setSelectedAvailable([]);
    setSelectedSelected([]);
    setSelectedFields(updatedSelectedFields);
    getSelectedList?.(updatedSelectedFields);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.ecaDocList.addField'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.DRAWER,
        action: t('gaTracking.ecaDocList.addListField'),
      },
      subPage: {
        subpage_name: t('gaTracking.ecaDocList.manageColumsSortOrder'),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
  }, [commonAvailableFields, getSelectedList, selectedFields]);

  const moveToAllAvailable = useCallback(() => {
    const updatedSelectedFields = difference(
      selectedFields,
      commonSelectedFields
    );
    setSelectedAvailable([]);
    setSelectedSelected([]);
    setSelectedFields(updatedSelectedFields);
    getSelectedList?.(updatedSelectedFields);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.ecaDocList.removeField'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.DRAWER,
        action: t('gaTracking.ecaDocList.removeListField'),
      },
      subPage: {
        subpage_name: t('gaTracking.ecaDocList.manageColumsSortOrder'),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
  }, [getSelectedList, selectedFields, commonSelectedFields]);

  const moveToSelected = useCallback(() => {
    const updated = selectedAvailable.filter(
      (f) => !selectedFields.some((s) => s.id === f.id)
    );
    const newSelectedFields = [...selectedFields, ...updated];
    setSelectedFields(newSelectedFields);
    setSelectedAvailable([]);
    getSelectedList?.(newSelectedFields);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.ecaDocList.addField'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.DRAWER,
        action: t('gaTracking.ecaDocList.addListField'),
      },
      subPage: {
        subpage_name: t('gaTracking.ecaDocList.manageColumsSortOrder'),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
  }, [getSelectedList, selectedAvailable, selectedFields]);

  const moveToAvailable = useCallback(() => {
    const newSelectedFields = selectedFields.filter(
      (f) => !selectedSelected.some((s) => s.id === f.id)
    );
    setSelectedFields(newSelectedFields);
    setSelectedSelected([]);
    getSelectedList?.(newSelectedFields);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.ecaDocList.removeField'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.DRAWER,
        action: t('gaTracking.ecaDocList.removeListField'),
      },
      subPage: {
        subpage_name: t('gaTracking.ecaDocList.manageColumsSortOrder'),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
  }, [getSelectedList, selectedFields, selectedSelected]);

  const handleSearchChange = useCallback(
    (search: string, type: ManageColFieldtType) => {
      if (type === 'available') {
        setSelectedAvailable([]);
        setSearchTermAvailable(search);
      } else {
        setSelectedSelected([]);
        setSearchTermSelected(search);
      }
    },
    []
  );

  const handleToggle = useCallback(
    (
      field: ArrayItemType,
      selectedList: ArrayItemType[],
      setSelectedList: React.Dispatch<React.SetStateAction<ArrayItemType[]>>
    ) => {
      if (field.name === 'confidence_score') return;

      setSelectedList((prev) =>
        prev.some((item) => item.id === field.id)
          ? prev.filter((item) => item.id !== field.id)
          : [...prev, field]
      );
    },
    []
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const isFieldSelected = useCallback(
    (field: ArrayItemType, selectedList: ArrayItemType[]) =>
      selectedList?.some((item) => item?.id === field?.id),
    []
  );

  const customList = (items: ArrayItemType[]) => (
    <List className="list-box">
      {items.map((value: ArrayItemType) => {
        const labelId = `transfer-list-all-item-${value.id}-label`;

        return (
          <ListItem disablePadding>
            <ListItemButton
              key={value.id}
              onClick={() =>
                handleToggle(value, selectedAvailable, setSelectedAvailable)
              }
              selected={isFieldSelected(value, selectedAvailable)}
              className="custom-list-item"
              data-testid={`available-${value.id}`}
            >
              <ListItemText className="custom-list-item-text" id={labelId}>
                <Typography className="typo-text body3_size">
                  {value.label}
                </Typography>
              </ListItemText>
            </ListItemButton>
          </ListItem>
        );
      })}
    </List>
  );

  return (
    <Box className="manageColFields-container">
      <Box className="manageColFields-box">
        <Box className="manageColFields">
          <Box className="list-container">
            <Box className="list-header">
              <Typography className="typo-text subhead4_size title">
                {t('ecaDocList.manageFieldsSort.availableListTitle')}
              </Typography>
              <AurSearchBox
                dataTestId={'available-search-textfield'}
                value={searchTermAvailable}
                autoComplete={'off'}
                classNameProps="searchbox-field"
                variant={'outlined'}
                label={''}
                placeHolder={t('common.search')}
                minCharForSearch={1}
                onSearch={(e) => handleSearchChange(e, 'available')}
              />
            </Box>
            <Box className="list-content">
              {customList(displayedAvailableFields)}
            </Box>
          </Box>
          <Box className="arrow-control">
            <div className="left-to-right">
              <IconButton
                disabled={isEmpty(displayedAvailableFields)}
                onClick={moveToAllSelected}
                className="shev-right"
                data-testid="move-all-available-button"
                aria-label={t('ecaDocList.manageFieldsSort.moveAllToRight')}
              >
                {isEmpty(displayedAvailableFields) ? (
                  <ArrowDoubbleRightInactive aria-hidden="true" />
                ) : (
                  <ArrowDoubbleRight aria-hidden="true" />
                )}
              </IconButton>
              <IconButton
                disabled={isEmpty(commonSelectedAvailableFields)}
                onClick={moveToSelected}
                className="shev-right"
                data-testid="move-available-button"
                aria-label={t('ecaDocList.manageFieldsSort.moveSelectedToRight')}
              >
                {!isEmpty(commonSelectedAvailableFields) ? (
                  <ArrowRightOutlinedIconActive aria-hidden="true" />
                ) : (
                  <ArrowRightOutlinedIcon aria-hidden="true" />
                )}
              </IconButton>
            </div>
            <Divider className="control-divider" />
            <div className="left-to-right">
              <IconButton
                disabled={isEmpty(commonSelectedSelectedFields)}
                onClick={moveToAvailable}
                className="shev-right"
                data-testid="move-selected-button"
                aria-label={t('ecaDocList.manageFieldsSort.moveSelectedToLeft')}
              >
                {!isEmpty(commonSelectedSelectedFields) ? (
                  <ArrowLeftOutlinedIconActive aria-hidden="true" />
                ) : (
                  <ArrowLeftOutlinedIcon aria-hidden="true" />
                )}
              </IconButton>
              <IconButton
                disabled={isEmpty(displayedSelectedFields)}
                onClick={moveToAllAvailable}
                className="shev-right"
                data-testid="move-all-selected-button"
                aria-label={t('ecaDocList.manageFieldsSort.moveAllToLeft')}
              >
                {isEmpty(displayedSelectedFields) ? (
                  <ArrowDoubbleLeft aria-hidden="true" />
                ) : (
                  <ArrowDoubbleLeftActive aria-hidden="true" />
                )}
              </IconButton>
            </div>
          </Box>
          <Box className="list-container">
            <Box className="list-header">
              <Typography className="typo-text subhead4_size title">
                {t('ecaDocList.manageFieldsSort.selectedListTitle')}
              </Typography>
              <AurSearchBox
                dataTestId={'selected-search-textfield'}
                value={searchTermSelected}
                autoComplete={'off'}
                classNameProps="searchbox-field"
                variant={'outlined'}
                label={''}
                placeHolder={t('common.search')}
                onSearch={(e) => handleSearchChange(e, 'selected')}
              />
            </Box>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={(e: DragEndEvent) => HandleDrag(e, setSelectedFields)}
            >
              <Box className="list-content">
                <List className="list-box">
                  <SortableContext
                    items={displayedSelectedFields}
                    strategy={verticalListSortingStrategy}
                  >
                    {displayedSelectedFields.map((item) => (
                      <SortableListItem
                        key={item.id}
                        item={item}
                        handleToggle={() =>
                          handleToggle(
                            item,
                            selectedSelected,
                            setSelectedSelected
                          )
                        }
                        selected={isFieldSelected(item, selectedSelected)}
                        elementType="button"
                        className="custom-list-item"
                      >
                        <ListItemText
                          className="custom-list-item-text"
                          data-testid={`selected-${item.id}`}
                        >
                          <OrderIcon aria-hidden="true" />
                          <Typography className="typo-text body3_size">
                            {item.label}
                          </Typography>
                        </ListItemText>
                      </SortableListItem>
                    ))}
                  </SortableContext>
                </List>
              </Box>
            </DndContext>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default ManageColFields;
